import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { X } from "lucide-react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format, eachDayOfInterval } from "date-fns";
import { DateRange } from "react-day-picker";

interface DateBlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (dates: string[]) => void;
  existingBlockedDates?: any[];
  editingBlock?: {
    id: number;
    date: string;
  } | null;
}

export function DateBlockModal({ isOpen, onClose, onSave, existingBlockedDates, editingBlock }: DateBlockModalProps) {
  const { toast } = useToast();
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  // Use passed blocked dates or fetch them
  const { data: fetchedBlockedDates = [] } = useQuery({
    queryKey: ["blocked-dates"],
    queryFn: async () => {
      const res = await fetch("/api/admin/blocked-dates");
      if (!res.ok) throw new Error("Failed to fetch blocked dates");
      return res.json();
    },
    enabled: !existingBlockedDates,
  });
  
  const blockedDates = existingBlockedDates || fetchedBlockedDates;

  useEffect(() => {
    if (editingBlock) {
      const date = new Date(editingBlock.date + 'T00:00:00');
      setDateRange({ from: date, to: date });
    } else {
      setDateRange(undefined);
    }
  }, [editingBlock]);


  const handleSave = () => {
    if (!dateRange?.from) {
      toast({
        title: "Error",
        description: "Please select at least one date to block",
        variant: "destructive",
      });
      return;
    }

    // Generate all dates in the range
    const dates = dateRange.to 
      ? eachDayOfInterval({ start: dateRange.from, end: dateRange.to })
      : [dateRange.from];

    console.log("Date range selected:", {
      from: dateRange.from,
      to: dateRange.to,
      fromString: format(dateRange.from, "yyyy-MM-dd EEEE"),
      toString: dateRange.to ? format(dateRange.to, "yyyy-MM-dd EEEE") : "N/A",
      dates: dates.map(d => format(d, "yyyy-MM-dd EEEE"))
    });

    // Filter out weekends and already blocked dates
    const validDates = dates.filter(date => {
      const day = date.getDay();
      const isWeekend = day === 0 || day === 6;
      const isAlreadyBlocked = isDateBlocked(date);
      return !isWeekend && !isAlreadyBlocked;
    });

    if (validDates.length === 0) {
      toast({
        title: "Error",
        description: "No valid dates to block in the selected range (weekends and already blocked dates are excluded)",
        variant: "destructive",
      });
      return;
    }

    // Format dates properly for storage - using local date formatting to avoid timezone shifts
    const dateStrings = validDates.map(d => {
      return format(d, "yyyy-MM-dd");
    });
    
    console.log("Dates to save:", dateStrings);
    
    onSave(dateStrings);
    
    toast({
      title: "Dates added",
      description: `Added ${validDates.length} date${validDates.length !== 1 ? 's' : ''} to be blocked`,
    });
    
    setDateRange(undefined);
    onClose();
  };

  // Check if a date is already blocked
  const isDateBlocked = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return blockedDates.some((blocked: any) => {
      if (!blocked.date) return false;
      
      // Handle different date formats
      let blockedDateStr: string;
      if (typeof blocked.date === 'string') {
        // Extract just the date part if it has time component
        blockedDateStr = blocked.date.split('T')[0];
      } else {
        // If it's a Date object or other format
        try {
          blockedDateStr = format(new Date(blocked.date), "yyyy-MM-dd");
        } catch {
          return false;
        }
      }
      
      return blockedDateStr === dateStr && (!editingBlock || blocked.id !== editingBlock.id);
    });
  };


  // Disable past dates and weekends
  const disabledDays = [
    { before: new Date() },
    (date: Date) => {
      const day = date.getDay();
      return day === 0 || day === 6; // Sunday or Saturday
    },
    (date: Date) => isDateBlocked(date),
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#171717] border-white/10 text-white max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">
              {editingBlock ? "Edit Blocked Date" : "Select dates to block"}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-gray-400 mb-4">
            Select a date range to block from bookings. Weekends will be automatically excluded.
          </p>

          <div className="flex justify-center">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              disabled={disabledDays}
              className="rounded-md border border-white/10"
              classNames={{
                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                month: "space-y-4",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-sm font-medium",
                nav: "space-x-1 flex items-center",
                nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse space-y-1",
                head_row: "flex",
                head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                row: "flex w-full mt-2",
                cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-white/10 rounded-md",
                day_selected: "bg-white text-black hover:bg-white hover:text-clubhouse-black focus:bg-white focus:text-black",
                day_today: "bg-white/10",
                day_outside: "text-muted-foreground opacity-50",
                day_disabled: "text-muted-foreground opacity-50 line-through",
                day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                day_hidden: "invisible",
              }}
            />
          </div>

          {dateRange?.from && (
            <div className="mt-4 p-3 bg-black/50 rounded-md">
              <p className="text-sm text-gray-400 mb-2">Selected range:</p>
              <div className="text-sm">
                {dateRange.to ? (
                  <>
                    {format(dateRange.from, "MMM d, yyyy")} - {format(dateRange.to, "MMM d, yyyy")}
                    {(() => {
                      try {
                        const dates = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
                        const validDates = dates.filter(date => {
                          const day = date.getDay();
                          const isWeekend = day === 0 || day === 6;
                          const isAlreadyBlocked = isDateBlocked(date);
                          return !isWeekend && !isAlreadyBlocked;
                        });
                        return (
                          <span className="text-gray-400 ml-2">
                            ({validDates.length} weekday{validDates.length !== 1 ? 's' : ''} to block)
                          </span>
                        );
                      } catch (error) {
                        console.error("Error calculating valid dates:", error);
                        return <span className="text-gray-400 ml-2">(calculating...)</span>;
                      }
                    })()}
                  </>
                ) : (
                  format(dateRange.from, "MMM d, yyyy")
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-white/20 hover:bg-white/10 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!dateRange?.from}
            className="bg-white hover:bg-white/90 text-black font-medium"
          >
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}